import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV } from "../data/site";
import { Logo } from "./Logo";
import { track } from "../lib/api";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const flatNav = useMemo(() => NAV.flatMap((item) => (item.children ? item.children : [item])), []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-brand-line shadow-sm" : "bg-white border-b border-transparent"}`} data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <button className="flex items-center gap-1 text-sm font-semibold text-brand-navy hover:text-brand-gold transition-colors">
                  {item.label} <ChevronDown className="h-4 w-4" />
                </button>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="w-56 rounded-xl border border-brand-line bg-white shadow-xl p-2">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block px-3 py-2 rounded-lg text-sm font-medium text-brand-navy hover:bg-brand-surface" data-testid={`nav-${c.to.slice(1)}`}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={item.to} to={item.to} className="text-sm font-semibold text-brand-navy hover:text-brand-gold transition-colors" data-testid={`nav-${item.to.slice(1)}`}>
                {item.label}
              </Link>
            )
          )}
        </nav>
        <div className="flex items-center gap-2">
          <a href="tel:0431206206" onClick={() => track("phone_tap", { where: "header" })} className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-gold px-5 py-2.5 text-sm font-bold text-brand-navy hover:bg-brand-goldDark hover:-translate-y-0.5 transition-all duration-200" data-testid="header-call-btn">
            <Phone className="h-4 w-4" /> 0431 206 206
          </a>
          <Link to="/sell-your-car" className="hidden md:inline-flex items-center rounded-full border-2 border-brand-navy px-5 py-2 text-sm font-bold text-brand-navy hover:bg-brand-navy hover:text-white transition-colors" data-testid="header-quote-btn">
            Get a price
          </Link>
          <button className="lg:hidden p-2 text-brand-navy" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-white" data-testid="mobile-nav">
          <div className="px-4 py-4 space-y-1">
            {flatNav.map((c) => (
              <Link key={c.to} to={c.to} className="block px-3 py-3 rounded-lg text-base font-semibold text-brand-navy hover:bg-brand-surface" data-testid={`mnav-${c.to.slice(1)}`}>
                {c.label}
              </Link>
            ))}
            <Link to="/sell-your-car" className="block mt-2 text-center rounded-full bg-brand-gold px-5 py-3 font-bold text-brand-navy" data-testid="mobile-quote-btn">
              Get a price for your car
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
